'use client';

import { useEffect, useRef, useState } from 'react';
import {
  loadBrowserPrint,
  getLocalPrinters,
  type BrowserPrintDeviceRaw
} from '@/lib/zebra';
import type { DiscoveredDevice } from '../types';

function parseHostStatus(response: string): {
  serial: string | null;
  firmware: string | null;
} {
  if (!response) return { serial: null, firmware: null };
  const serialMatch = response.match(
    /(?:serial(?:\s*#?|number)?|S\/N)\D*([A-Z0-9-]+)/i
  );
  const firmwareMatch = response.match(
    /(?:firmware|version|V)[^\dA-Z]*([\dA-Z._-]+)/i
  );
  return {
    serial: serialMatch?.[1] ?? null,
    firmware: firmwareMatch?.[1] ?? null
  };
}

async function fetchPrinterMetadata(
  device: BrowserPrintDeviceRaw
): Promise<{ serial: string | null; firmware: string | null }> {
  if (!device.sendThenRead) return { serial: null, firmware: null };
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: {
      serial: string | null;
      firmware: string | null;
    }) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(
      () => settle({ serial: null, firmware: null }),
      2000
    );
    device.sendThenRead!(
      '~HQHA',
      (response) => {
        clearTimeout(timer);
        settle(parseHostStatus(response));
      },
      () => {
        clearTimeout(timer);
        settle({ serial: null, firmware: null });
      }
    );
  });
}

export type PrinterDiscoveryStatus =
  | 'loading'
  | 'ready'
  | 'service-missing'
  | 'error';

export interface DiscoveredPrinter extends DiscoveredDevice {
  uid: string;
}

export interface UsePrinterDiscoveryResult {
  status: PrinterDiscoveryStatus;
  devices: DiscoveredPrinter[];
  error: string | null;
  reload: () => void;
}

export interface UsePrinterDiscoveryOptions {
  pollMs?: number;
}

export function usePrinterDiscovery(
  enabled: boolean,
  { pollMs }: UsePrinterDiscoveryOptions = {}
): UsePrinterDiscoveryResult {
  const [status, setStatus] = useState<PrinterDiscoveryStatus>('loading');
  const [devices, setDevices] = useState<DiscoveredPrinter[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when enabled/reloadKey changes
    setStatus('loading');

    setError(null);

    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const probe = async () => {
      try {
        await loadBrowserPrint();
      } catch (err) {
        if (cancelledRef.current) return;
        setStatus('service-missing');
        setError(err instanceof Error ? err.message : String(err));
        setDevices([]);
        schedule();
        return;
      }

      try {
        const list = await getLocalPrinters();
        if (cancelledRef.current) return;

        const enriched = await Promise.all(
          list.map(async (d) => {
            const meta = await fetchPrinterMetadata(d);
            const isNetwork =
              (d.connection ?? '').toLowerCase() === 'network' ||
              (d.uid ?? '').includes('.');
            return {
              uid: d.uid ?? '',
              device_type: 'printer' as const,
              name: d.name ?? d.uid ?? 'Zebra Printer',
              scanner_identifier: null,
              serial_number: meta.serial,
              ip_address: isNetwork ? (d.uid ?? null) : null,
              software_version: meta.firmware
            };
          })
        );
        if (cancelledRef.current) return;
        setDevices(enriched);
        setStatus('ready');
        setError(null);
      } catch (err) {
        if (cancelledRef.current) return;
        setStatus('service-missing');
        setError(err instanceof Error ? err.message : String(err));
        setDevices([]);
      }
      schedule();
    };

    const schedule = () => {
      if (cancelledRef.current || !pollMs) return;
      pollTimer = setTimeout(() => void probe(), pollMs);
    };

    void probe();

    return () => {
      cancelledRef.current = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [enabled, reloadKey, pollMs]);

  return {
    status,
    devices,
    error,
    reload: () => setReloadKey((k) => k + 1)
  };
}
